import { useMatch } from 'react-router-dom';
import { useRef, useState, useEffect, useCallback } from 'react';

import { AppRoutes } from 'config';
import { useSocket } from 'context/socket';
import type { Reader } from 'store/api/clover/reader-status';
import { useReaderStatusListQuery } from 'store/api/clover/reader-status';

// Live reader dots (owner 24 Oct 2026: "always refreshing ... keep it live").
//  * Push first: the server sends 'reader-status-changed' the moment a reader
//    is taken, let go, starts or ends a card sale, or changes status; every
//    iPad re-reads at once.
//  * Then a read every 5 s while the screen is visible, and right away on
//    focus, when the iPad is back online, and when the socket reconnects.
//  * The read is the server's own data only (?cached=1): it never asks Clover
//    and never writes (Clover checks stay with the server's sweep), so it is
//    safe beside a sale.
//  * A failed read never blanks the dots: the last good list stays for 60 s.
//    A list older than 15 s is "stale" (the picker greys it out and blocks
//    nothing on it; the server still refuses a reader in use).
export const READER_POLL_MS = 5000;
export const READER_KEEP_MS = 60_000;
export const READER_STALE_MS = 15_000;
const PUSH_DEBOUNCE_MS = 200;
const REFOCUS_GAP_MS = 1000;

const isVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

export interface LiveReaders {
  readers?: Reader[];
  // The last good list is older than READER_STALE_MS.
  stale: boolean;
  // The live channel (socket) is down; dots come from the 5 s read only.
  reconnecting: boolean;
  // When the shown list was read (ms), null before the first answer.
  readAt: number | null;
  // The latest read failed (the last good list may still be shown).
  failed: boolean;
  refetch: () => void;
}

export const useLiveReaders = (
  eventId?: number,
  { skip = false }: { skip?: boolean } = {}
): LiveReaders => {
  const off = !!skip || !eventId;
  const [visible, setVisible] = useState(isVisible);
  const [now, setNow] = useState(() => Date.now());
  const { socket, subscribe } = useSocket();
  const [connected, setConnected] = useState(() => !!socket?.connected);

  const query = useReaderStatusListQuery(
    eventId ? { eventId, cached: true } : undefined,
    {
      skip: off,
      refetchOnMountOrArgChange: true,
      pollingInterval: visible && !off ? READER_POLL_MS : 0,
    }
  );
  const { refetch, isUninitialized, data, fulfilledTimeStamp, isError } = query;

  // The mount itself reads (refetchOnMountOrArgChange): no second read at once.
  const lastAt = useRef(Date.now());
  const refresh = useCallback(() => {
    if (off || isUninitialized) {
      return;
    }
    lastAt.current = Date.now();
    try {
      refetch();
    } catch {
      // Display only.
    }
  }, [off, isUninitialized, refetch]);

  // Focus / visible again / back online: read now (at most once a second),
  // and wake a socket that gave up.
  useEffect(() => {
    if (off) {
      return;
    }
    const onChange = () => {
      const nowVisible = isVisible();
      setVisible(nowVisible);
      if (!nowVisible) {
        return;
      }
      if (socket && !socket.connected) {
        try {
          socket.connect();
        } catch {
          // The 5 s read goes on.
        }
      }
      if (Date.now() - lastAt.current >= REFOCUS_GAP_MS) {
        refresh();
      }
    };
    window.addEventListener('focus', onChange);
    window.addEventListener('online', onChange);
    document.addEventListener('visibilitychange', onChange);
    return () => {
      window.removeEventListener('focus', onChange);
      window.removeEventListener('online', onChange);
      document.removeEventListener('visibilitychange', onChange);
    };
  }, [off, refresh, socket]);

  // Push: re-read the moment a reader changes (debounced; one read for a burst).
  useEffect(() => {
    if (off) {
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = subscribe('reader-status-changed', () => {
      clearTimeout(timer);
      timer = setTimeout(refresh, PUSH_DEBOUNCE_MS);
    });
    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, [off, subscribe, refresh, socket]);

  // The live channel's state, and a read right after it comes back.
  useEffect(() => {
    if (off || !socket) {
      return;
    }
    setConnected(!!socket.connected);
    const onConnect = () => {
      setConnected(true);
      refresh();
    };
    const onDisconnect = () => setConnected(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [off, socket, refresh]);

  // A clock for "stale" / "kept for 60 s".
  useEffect(() => {
    if (off) {
      return;
    }
    const tick = setInterval(() => setNow(Date.now()), READER_POLL_MS);
    return () => clearInterval(tick);
  }, [off]);

  const readAt = fulfilledTimeStamp ?? null;
  const clock = Math.max(now, readAt ?? 0);
  const age = readAt == null ? Infinity : clock - readAt;
  const readers =
    off || readAt == null || age > READER_KEEP_MS ? undefined : data?.readers;
  return {
    readAt,
    readers,
    failed: !off && !!isError,
    refetch: refresh,
    stale: !!readers && age > READER_STALE_MS,
    reconnecting: !off && !!socket && !connected,
  };
};

// The cart's picker and one-reader chip: only on the products page itself
// (/products/:id, exact). Checkout has no picker; its reader pop-up reads
// for itself while it is open (ReaderChoice).
export const useEventReaders = (eventId?: number, skip?: boolean) => {
  const onProductsPage = !!useMatch({
    end: true,
    path: AppRoutes.Products.Home,
  });
  return useLiveReaders(eventId, { skip: !!skip || !onProductsPage });
};
